import type { IsoWeekday } from '@codot/contracts';
import { ui, uiLocale } from '../i18n';

/** Phase 17.7: Western digits in a string → Arabic-Indic when the UI is Arabic with Arabic digits. */
export const digits = (s: string): string =>
  ui.lang === 'ar' && ui.numerals === 'arab' ? s.replace(/[0-9]/g, (d) => '٠١٢٣٤٥٦٧٨٩'[Number(d)]!) : s;

/** Short weekday in the UI language (1 = Mon … 7 = Sun); 2024-01-01 was a Monday. */
const weekdayShort = (d: number): string =>
  new Intl.DateTimeFormat(uiLocale(), { weekday: 'short', timeZone: 'UTC' }).format(Date.UTC(2024, 0, d));

const UNITS = { en: ['B', 'KB', 'MB', 'GB', 'TB'], ar: ['بايت', 'ك.ب', 'م.ب', 'ج.ب', 'ت.ب'] } as const;
export function formatBytes(bytes: number): string {
  const units = UNITS[ui.lang];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${new Intl.NumberFormat(uiLocale(), { maximumFractionDigits: i === 0 ? 0 : 1, minimumFractionDigits: i === 0 ? 0 : 1 }).format(value)} ${units[i]}`;
}

/**
 * The week as Displaro's users count it: Saturday first (Gulf week; legacy CODOT and the region's calendars).
 * Days stay ISO numbers on the wire (1 = Mon … 7 = Sun); only display order and pickers follow this.
 * (Phase 16 Step 7, gap §14 #8.)
 */
export const WEEK_ORDER: readonly IsoWeekday[] = [6, 7, 1, 2, 3, 4, 5];
export const weekdayName = (d: number): string => (d >= 1 && d <= 7 ? weekdayShort(d) : `?${d}`);
/** Sorts ISO weekdays into WEEK_ORDER (Sat … Fri). */
export const inWeekOrder = (days: readonly number[]): number[] =>
  [...days].sort((a, b) => WEEK_ORDER.indexOf(a as IsoWeekday) - WEEK_ORDER.indexOf(b as IsoWeekday));

/** ISO weekday 1 = Mon … 7 = Sun, listed Saturday first. */
export const formatWeekdays = (days: readonly IsoWeekday[]): string => inWeekOrder(days).map(weekdayName).join(', ');

export const formatMs = (ms: number): string =>
  digits(ms >= 1000 ? `${(ms / 1000).toFixed(ms % 1000 ? 1 : 0)} ${ui.lang === 'ar' ? 'ث' : 's'}` : `${ms} ${ui.lang === 'ar' ? 'م.ث' : 'ms'}`);

export const shortHash = (sha: string): string => `${sha.slice(0, 8)}…${sha.slice(-4)}`;

/** "just now", "5 min ago", "3 h ago", "2 d ago" — compact relative time for dense tables. */
export function formatAgo(iso: string, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (ui.lang !== 'en') {
    const rtf = new Intl.RelativeTimeFormat(uiLocale(), { numeric: 'auto', style: 'short' });
    if (s < 45) return rtf.format(0, 'second');
    if (s < 3600) return rtf.format(-Math.round(s / 60), 'minute');
    if (s < 86_400) return rtf.format(-Math.round(s / 3600), 'hour');
    return rtf.format(-Math.round(s / 86_400), 'day');
  }
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86_400)} d ago`;
}

/** Play length as a clock: "45 s", "2:05", "1:02:05" (playlist totals). */
export function formatLength(ms: number): string {
  const total = Math.round(ms / 1000);
  if (total < 60) return digits(`${total} ${ui.lang === 'ar' ? 'ث' : 's'}`);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return digits(h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`);
}
